import { useRef, useState } from 'react';
import { getFormPlaceholder } from '@/shared/copy/forms';
import { componentSizes } from '@/theme/componentSizes';
import { s } from '@/theme/tokens';
import {
  Box,
  Cancel,
  CloseIcon,
  IconButton,
  InputAdornment,
  SearchIcon,
  TextField,
} from '@/ui/primitives';
import {
  buildSearchFilterClearButtonSx,
  buildSearchFilterHeightSx,
  filterInputSx,
  searchFilterEndAdornmentSx,
  searchFilterStartAdornmentSx,
  searchFilterTrailingIconSx,
  searchFilterTypographySx,
} from './filterControlStyles';
import type { TextFieldProps } from '@/ui/primitives';
import type { FocusEvent, MouseEvent, PointerEvent, ReactElement } from 'react';

type SearchFilterFieldProps = Omit<TextFieldProps, 'value' | 'label'> & {
  value: string;
  label?: TextFieldProps['label'];
  inputAriaLabel?: string;
  compact?: boolean;
  controlHeight?: number;
  leadingSearchAdornment?: boolean;
  trailingSearchAdornment?: boolean;
  filledClearIcon?: boolean;
  onClear?: () => void;
};

export const SearchFilterField = ({
  value,
  label,
  inputAriaLabel,
  compact = false,
  controlHeight,
  leadingSearchAdornment = false,
  trailingSearchAdornment = true,
  filledClearIcon = false,
  onClear,
  inputBackgroundColor = 'var(--control-surface)',
  placeholder = getFormPlaceholder('search'),
  slotProps,
  sx,
  ...rest
}: SearchFilterFieldProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const clearedFromPointerRef = useRef(false);
  const resolvedHeight = controlHeight ?? componentSizes.filters.controlHeight;
  const resolvedPaddingY = compact ? s('none') : s('xxs');
  const resolvedPaddingLeft = leadingSearchAdornment ? s('xxs') : `calc(${s('md')} + ${s('xs')})`;
  const showClear = value.length > 0;
  const hasValue = value.length > 0;
  const [isInputFocused, setIsInputFocused] = useState(false);
  const clearInput = (): void => {
    onClear?.();
    const inputElement = inputRef.current;
    if (!inputElement) return;
    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(
      inputElement,
      '',
    );
    inputElement.dispatchEvent(new Event('input', { bubbles: true }));
    inputElement.focus();
  };
  const handleClearPointerDown = (event: PointerEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    clearedFromPointerRef.current = true;
    clearInput();
  };
  const handleClearMouseDown = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };
  const handleClearClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (clearedFromPointerRef.current) {
      clearedFromPointerRef.current = false;
      return;
    }
    clearInput();
  };
  const maxCollapsedChars = 64;
  const displayValue =
    !isInputFocused && value.length > maxCollapsedChars
      ? `${value.slice(0, maxCollapsedChars - 1)}...`
      : value;
  const resolvedPaddingRight = showClear ? `calc(${s('md')} + ${s('xxs')})` : s('lg');
  const startAdornment = leadingSearchAdornment ? (
    <InputAdornment position="start" sx={searchFilterStartAdornmentSx}>
      <SearchIcon fontSize="small" />
    </InputAdornment>
  ) : null;
  const endAdornment = (
    <InputAdornment position="end" sx={searchFilterEndAdornmentSx}>
      {showClear ? (
        <IconButton
          aria-label="Clear"
          title="Clear"
          edge="end"
          size="small"
          className="search-clear-button"
          onPointerDown={handleClearPointerDown}
          onMouseDown={handleClearMouseDown}
          onClick={handleClearClick}
          sx={buildSearchFilterClearButtonSx(filledClearIcon)}
        >
          {filledClearIcon ? <Cancel fontSize="small" /> : <CloseIcon fontSize="small" />}
        </IconButton>
      ) : null}
      {trailingSearchAdornment ? (
        <Box sx={searchFilterTrailingIconSx} aria-hidden="true">
          <SearchIcon fontSize="inherit" />
        </Box>
      ) : null}
    </InputAdornment>
  );
  const baseSlotProps = typeof slotProps === 'function' ? {} : (slotProps ?? {});
  const mergedSlotProps: TextFieldProps['slotProps'] = {
    ...baseSlotProps,
    input: (ownerState) => {
      const resolved =
        typeof baseSlotProps.input === 'function'
          ? baseSlotProps.input(ownerState)
          : baseSlotProps.input;
      return {
        ...resolved,
        startAdornment: resolved?.startAdornment ?? startAdornment,
        endAdornment: resolved?.endAdornment ?? endAdornment,
      };
    },
    htmlInput: (ownerState) => {
      const resolved =
        typeof baseSlotProps.htmlInput === 'function'
          ? baseSlotProps.htmlInput(ownerState)
          : baseSlotProps.htmlInput;
      const resolvedLabel =
        resolved?.['aria-label'] ??
        inputAriaLabel ??
        (typeof label === 'string' ? label : undefined);
      return {
        ...resolved,
        ref: (instance: HTMLInputElement | null) => {
          if (typeof resolved?.ref === 'function') {
            resolved.ref(instance);
          } else if (resolved?.ref && typeof resolved.ref === 'object') {
            resolved.ref.current = instance;
          }
          inputRef.current = instance;
        },
        style: {
          ...resolved?.style,
          paddingLeft: resolvedPaddingLeft,
          paddingInlineStart: resolvedPaddingLeft,
        },
        onFocus: (event: FocusEvent<HTMLInputElement>) => {
          resolved?.onFocus?.(event);
          setIsInputFocused(true);
        },
        onBlur: (event: FocusEvent<HTMLInputElement>) => {
          resolved?.onBlur?.(event);
          setIsInputFocused(false);
        },
        'aria-label': resolvedLabel,
      };
    },
  };
  const heightSx = buildSearchFilterHeightSx({
    hasValue,
    resolvedHeight,
    resolvedPaddingLeft,
    resolvedPaddingRight,
    resolvedPaddingY,
  });

  return (
    <TextField
      {...rest}
      label={label}
      placeholder={placeholder}
      value={displayValue}
      focused={hasValue || rest.focused === true}
      size="small"
      fieldStyle="filter"
      inputHeight={resolvedHeight}
      inputBackgroundColor={inputBackgroundColor}
      slotProps={mergedSlotProps}
      sx={
        [filterInputSx, searchFilterTypographySx, heightSx, sx].filter(
          Boolean,
        ) as TextFieldProps['sx']
      }
    />
  );
};
